import * as React from 'react';

/**
 * Tab variant types
 * - underline: Classic tab with bottom border indicator
 * - contained: Tab with background, active tab has elevated background
 */
export type TabsVariant = 'underline' | 'contained';

export interface TabsProps {
  /**
   * Controlled value - the currently active tab
   */
  value?: string;
  /**
   * Default value for uncontrolled mode
   */
  defaultValue?: string;
  /**
   * Callback when the active tab changes
   */
  onChange?: (value: string) => void;
  /**
   * Visual variant of the tabs
   * @default 'underline'
   */
  variant?: TabsVariant;
  /**
   * Tab components (TabList and TabPanels)
   */
  children: React.ReactNode;
}

export interface TabListProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Tab components
   */
  children: React.ReactNode;
  /**
   * Additional CSS classes
   */
  className?: string;
}

export interface TabProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Unique value identifying this tab
   */
  value: string;
  /**
   * Whether this tab is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Tab label content
   */
  children: React.ReactNode;
  /**
   * Additional CSS classes
   */
  className?: string;
}

export interface TabPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Value identifying which tab this panel belongs to
   */
  value: string;
  /**
   * Panel content
   */
  children: React.ReactNode;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Context for sharing tab state between components
 */
interface TabsContextValue {
  value: string | undefined;
  onChange: (value: string) => void;
  variant: TabsVariant;
  registerTab: (value: string, ref: HTMLButtonElement | null) => void;
  unregisterTab: (value: string) => void;
  tabRefs: Map<string, HTMLButtonElement | null>;
  tabValues: string[];
}

const TabsContext = React.createContext<TabsContextValue | null>(null);

/**
 * Hook to access Tabs context
 */
function useTabsContext() {
  const context = React.useContext(TabsContext);
  if (!context) {
    throw new Error('Tab components must be used within a Tabs component');
  }
  return context;
}

/**
 * Tabs root component
 *
 * A compound component for organizing content into tabbed sections.
 * Supports keyboard navigation and both controlled and uncontrolled modes.
 *
 * @example
 * // Basic tabs
 * <Tabs defaultValue="tab1">
 *   <Tabs.List>
 *     <Tabs.Tab value="tab1">Tab 1</Tabs.Tab>
 *     <Tabs.Tab value="tab2">Tab 2</Tabs.Tab>
 *   </Tabs.List>
 *   <Tabs.Panel value="tab1">Content 1</Tabs.Panel>
 *   <Tabs.Panel value="tab2">Content 2</Tabs.Panel>
 * </Tabs>
 *
 * @example
 * // Controlled tabs
 * const [activeTab, setActiveTab] = React.useState('overview');
 * <Tabs value={activeTab} onChange={setActiveTab}>
 *   <Tabs.List>
 *     <Tabs.Tab value="overview">Overview</Tabs.Tab>
 *     <Tabs.Tab value="details">Details</Tabs.Tab>
 *   </Tabs.List>
 *   <Tabs.Panel value="overview">Overview content</Tabs.Panel>
 *   <Tabs.Panel value="details">Details content</Tabs.Panel>
 * </Tabs>
 */
function TabsRoot({
  value: controlledValue,
  defaultValue,
  onChange,
  variant = 'underline',
  children,
}: Readonly<TabsProps>) {
  const [uncontrolledValue, setUncontrolledValue] = React.useState(defaultValue);
  const [tabValues, setTabValues] = React.useState<string[]>([]);
  const tabRefsRef = React.useRef<Map<string, HTMLButtonElement | null>>(new Map());

  const isControlled = controlledValue !== undefined;
  const currentValue = isControlled ? controlledValue : uncontrolledValue;

  const handleChange = React.useCallback(
    (newValue: string) => {
      if (!isControlled) {
        setUncontrolledValue(newValue);
      }
      onChange?.(newValue);
    },
    [isControlled, onChange]
  );

  const registerTab = React.useCallback((tabValue: string, ref: HTMLButtonElement | null) => {
    tabRefsRef.current.set(tabValue, ref);
    setTabValues((prev) => {
      if (!prev.includes(tabValue)) {
        return [...prev, tabValue];
      }
      return prev;
    });
  }, []);

  const unregisterTab = React.useCallback((tabValue: string) => {
    tabRefsRef.current.delete(tabValue);
    setTabValues((prev) => prev.filter((v) => v !== tabValue));
  }, []);

  const contextValue: TabsContextValue = React.useMemo(
    () => ({
      value: currentValue,
      onChange: handleChange,
      variant,
      registerTab,
      unregisterTab,
      tabRefs: tabRefsRef.current,
      tabValues,
    }),
    [currentValue, handleChange, variant, registerTab, unregisterTab, tabValues]
  );

  return <TabsContext.Provider value={contextValue}>{children}</TabsContext.Provider>;
}

TabsRoot.displayName = 'Tabs';

/**
 * TabList component
 *
 * Container for tab buttons. Provides keyboard navigation.
 */
const TabList = React.forwardRef<HTMLDivElement, TabListProps>(
  ({ className = '', children, ...props }, ref) => {
    const { variant, value, onChange, tabRefs, tabValues } = useTabsContext();

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent) => {
      const enabledTabs = tabValues.filter((tabValue) => {
        const tabRef = tabRefs.get(tabValue);
        return tabRef && !tabRef.disabled;
      });

      if (enabledTabs.length === 0) {
        return;
      }

      const currentIndex = value ? enabledTabs.indexOf(value) : -1;
      let newIndex: number | null = null;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          newIndex = currentIndex <= 0 ? enabledTabs.length - 1 : currentIndex - 1;
          break;
        case 'ArrowRight':
          e.preventDefault();
          newIndex = currentIndex >= enabledTabs.length - 1 ? 0 : currentIndex + 1;
          break;
        case 'Home':
          e.preventDefault();
          newIndex = 0;
          break;
        case 'End':
          e.preventDefault();
          newIndex = enabledTabs.length - 1;
          break;
      }

      if (newIndex !== null) {
        const newValue = enabledTabs[newIndex];
        /* v8 ignore start */
        if (newValue !== undefined) {
          /* v8 ignore stop */
          onChange(newValue);
          const tabRef = tabRefs.get(newValue);
          tabRef?.focus();
        }
      }
    };

    const baseClasses =
      variant === 'underline'
        ? 'flex border-b border-secondary-200'
        : 'flex bg-secondary-100 rounded-t-lg p-1';

    const combinedClasses = [baseClasses, className].filter(Boolean).join(' ');

    return (
      <div
        ref={ref}
        role="tablist"
        tabIndex={-1}
        className={combinedClasses}
        onKeyDown={handleKeyDown}
        {...props}
      >
        {children}
      </div>
    );
  }
);

TabList.displayName = 'Tabs.List';

/**
 * Tab component
 *
 * Individual tab button.
 */
const Tab = React.forwardRef<HTMLButtonElement, TabProps>(
  ({ value: tabValue, disabled = false, className = '', children, ...props }, ref) => {
    const { value, onChange, variant, registerTab, unregisterTab } = useTabsContext();
    const internalRef = React.useRef<HTMLButtonElement | null>(null);

    const isActive = value === tabValue;

    // Register/unregister tab
    React.useEffect(() => {
      registerTab(tabValue, internalRef.current);
      return () => unregisterTab(tabValue);
    }, [tabValue, registerTab, unregisterTab]);

    // Combine refs
    const handleRef = React.useCallback(
      (node: HTMLButtonElement | null) => {
        internalRef.current = node;
        registerTab(tabValue, node);
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref, registerTab, tabValue]
    );

    const handleClick = () => {
      /* v8 ignore start */
      if (!disabled) {
        /* v8 ignore stop */
        onChange(tabValue);
      }
    };

    // Base styles for all tabs
    const baseClasses = 'relative h-11 px-4 text-[13px] font-medium transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 focus-visible:ring-offset-2';

    // Variant-specific styles
    let variantClasses: string;
    if (variant === 'underline') {
      if (disabled) {
        variantClasses = 'text-secondary-400 cursor-not-allowed';
      } else if (isActive) {
        variantClasses = 'text-primary-700';
      } else {
        variantClasses = 'text-secondary-500 hover:text-secondary-700';
      }
    } else if (disabled) {
      // contained variant
      variantClasses = 'text-secondary-400 cursor-not-allowed rounded-md';
    } else if (isActive) {
      variantClasses = 'text-primary-700 bg-secondary-50 rounded-md shadow-sm';
    } else {
      variantClasses = 'text-secondary-500 hover:text-secondary-700 rounded-md hover:bg-secondary-200/50';
    }

    const combinedClasses = [baseClasses, variantClasses, className].filter(Boolean).join(' ');

    return (
      <button
        ref={handleRef}
        role="tab"
        type="button"
        id={`tab-${tabValue}`}
        aria-selected={isActive}
        aria-controls={`tabpanel-${tabValue}`}
        tabIndex={isActive ? 0 : -1}
        disabled={disabled}
        className={combinedClasses}
        onClick={handleClick}
        {...props}
      >
        {children}
        {/* Underline indicator for underline variant */}
        {variant === 'underline' && isActive && (
          <span
            className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-700"
            style={{
              animation: 'tabIndicatorSlide 150ms ease-out',
            }}
          />
        )}
      </button>
    );
  }
);

Tab.displayName = 'Tabs.Tab';

/**
 * TabPanel component
 *
 * Content panel associated with a tab.
 */
const TabPanel = React.forwardRef<HTMLDivElement, TabPanelProps>(
  ({ value: panelValue, className = '', children, ...props }, ref) => {
    const { value } = useTabsContext();

    const isActive = value === panelValue;

    if (!isActive) {
      return null;
    }

    const combinedClasses = ['py-4', className].filter(Boolean).join(' ');

    return (
      <div
        ref={ref}
        role="tabpanel"
        id={`tabpanel-${panelValue}`}
        aria-labelledby={`tab-${panelValue}`}
        tabIndex={0}
        className={combinedClasses}
        {...props}
      >
        {children}
      </div>
    );
  }
);

TabPanel.displayName = 'Tabs.Panel';

/**
 * Tabs compound component
 *
 * Usage:
 * - Tabs.List - Container for tab buttons
 * - Tabs.Tab - Individual tab button
 * - Tabs.Panel - Content panel for a tab
 */
const Tabs = Object.assign(TabsRoot, {
  List: TabList,
  Tab: Tab,
  Panel: TabPanel,
});

export { Tabs, TabList, Tab, TabPanel };
